import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import LandingTeaser from "../components/LandingTeaser";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Users, Clock, CurrencyInr, Target, ChartLineUp, Sparkle, ArrowRight,
  ShieldCheck, MapPinArea, Airplane, Star, MegaphoneSimple, Ticket, Package,
  ChartBar, Gift, Kanban, Briefcase, Question, TrendUp, GraduationCap,
  Rocket, Buildings, CheckCircle, GitBranch, TreeStructure, CalendarCheck, Warning,
  Table, UploadSimple, ChatCircleText, GlobeHemisphereEast, LockKey, PlayCircle,
  Moon, Sun,
} from "@phosphor-icons/react";

const PERSONAS = [
  {
    icon: Buildings,
    tone: "teal",
    tag: "HR agencies",
    title: "Serve every client from one workspace.",
    text: "Multi-tenant by design. Isolated data per client, unified operations for your consultants. White-label with the client's logo & colours.",
    bullets: ["Multi-client staffing", "Per-tenant policies & letters", "Consolidated ops dashboard"],
  },
  {
    icon: Target,
    tone: "magenta",
    tag: "HR Heads / CHRO",
    title: "Run all of HR from one modern platform.",
    text: "Attendance, payroll, statutory, performance, culture, exit — no more juggling five tools. Board-ready analytics baked in.",
    bullets: ["Attendance → payroll flow", "OKRs + KRAs + 360°", "Reports library & audit log"],
  },
  {
    icon: Sparkle,
    tone: "green",
    tag: "Employees",
    title: "Self-serve everything, in seconds.",
    text: "Check-in with geo + selfie. Apply leave in one tap. Download payslip as PDF. Ask Zenith360 AI anything — from casual leave rules to your PF balance.",
    bullets: ["Beautiful mobile-first UI", "Ask Zenith360 AI copilot", "Payslip PDF, kudos, celebrations"],
  },
];

const STATS = [
  { n: "8", label: "core modules" },
  { n: "50+", label: "HR flows automated" },
  { n: "India-first", label: "statutory compliance" },
  { n: "24/7", label: "self-serve for employees" },
];

const HOW_IT_WORKS = [
  {
    n: "01",
    icon: Rocket,
    tone: "teal",
    title: "Onboard in minutes",
    text: "Answer 6 questions in the Setup Wizard — industry, headcount, business type. Our AI drafts your policies, grade structure, salary split, and holiday calendar tuned to your sector.",
  },
  {
    n: "02",
    icon: UploadSimple,
    tone: "magenta",
    title: "Bring your people in",
    text: "Bulk-upload employees, clients & projects via CSV. Or create them manually with band + grade + reporting manager. Auto-provision logins with role-based access.",
  },
  {
    n: "03",
    icon: PlayCircle,
    tone: "green",
    title: "Run every HR flow",
    text: "Attendance, payroll runs, statutory filings, performance cycles, engagement pulses — all live from day one. Multi-level approvals wired end-to-end.",
  },
];

const COMPLIANCE_ITEMS = [
  { t: "PF ECR", d: "Auto-computed EPFO ECR file, ready for portal upload.", icon: CurrencyInr },
  { t: "ESIC", d: "Wage-ceiling aware ESIC returns with employer + employee splits.", icon: ShieldCheck },
  { t: "Professional Tax", d: "State-wise slabs, monthly + annual returns.", icon: MapPinArea },
  { t: "TDS", d: "New + old regime, Form 16 export, quarterly Form 24Q.", icon: ChartBar },
  { t: "POSH Act 2013", d: "Statutory policy templates + audit-log of acknowledgements.", icon: LockKey },
  { t: "Shops & Establishments", d: "State-specific leave / notice period defaults.", icon: Buildings },
];

const FAQS = [
  { q: "How is Zenith360 HR different from Keka / Zoho / Darwinbox?", a: "Built by an HR outsourcing agency for HR outsourcing agencies. True multi-tenant, deep India statutory (PF/ESIC/PT/TDS ready out-of-box), and Claude Sonnet 4.5 AI woven into every workflow — from policy drafting to payslip explanations to Ask Zenith360." },
  { q: "How long does implementation take?", a: "The Setup Wizard drafts your policies + grades + salary split + holidays in under 30 seconds. Add employees via CSV bulk-upload and you're live the same day. Full go-live typically 3-7 days depending on integrations." },
  { q: "Can I white-label for each client tenant?", a: "Yes. Each tenant has its own logo, primary colour, tagline, letter templates, policies, and salary defaults. Employees see their company's brand, not ours." },
  { q: "Is my data secure?", a: "Data isolated per tenant at the database layer. HTTPS everywhere. Role-based access control down to the field level. Object storage for documents lives in Emergent-managed encrypted buckets." },
  { q: "What LLM powers Ask Zenith360 AI?", a: "Claude Sonnet 4.5 via the Emergent Universal Key — no LLM API setup on your side. The assistant is HR-tuned and knows Indian statutes (POSH, IT Act, Shops & Establishments, DPDP 2023)." },
  { q: "Does it work offline for field employees?", a: "Web is online-only, but the mobile-friendly UI works over 3G. A React Native native shell is on the roadmap." },
];

// Reusable tone → tailwind class helper
const TONE = {
  teal: { bg: "bg-zenith-teal/10", border: "border-zenith-teal/40", text: "text-zenith-teal", dot: "bg-zenith-teal" },
  magenta: { bg: "bg-zenith-magenta/10", border: "border-zenith-magenta/40", text: "text-zenith-magenta", dot: "bg-zenith-magenta" },
  green: { bg: "bg-zenith-green/10", border: "border-zenith-green/40", text: "text-zenith-green", dot: "bg-zenith-green" },
  blue: { bg: "bg-zenith-blue-500/10", border: "border-zenith-blue-500/40", text: "text-zenith-blue-500", dot: "bg-zenith-blue-500" },
};

const CATEGORIES = [
  {
    title: "Core HR",
    tagline: "Everything about your people",
    icon: Users,
    color: "from-adb-navy/10 to-adb-navy/5",
    items: [
      { icon: Users, name: "Employee master + org chart", text: "Full 360° employee record, custom fields, org hierarchy, and reporting lines." },
      { icon: ShieldCheck, name: "Documents vault", text: "Aadhaar, PAN, degrees — expiry alerts. Emergent object storage." },
      { icon: Kanban, name: "Onboarding & offboarding", text: "Role-owned checklists, workflows, welcome kits, exit clearance." },
    ],
  },
  {
    title: "Time & Attendance",
    tagline: "Track hours the modern way",
    icon: Clock,
    color: "from-adb-green/10 to-white",
    items: [
      { icon: MapPinArea, name: "Geo + selfie check-in", text: "GPS-fenced attendance with selfie verification and shift awareness." },
      { icon: Clock, name: "Shifts & rosters", text: "Day/night/split shift templates, roster planning, comp-off." },
      { icon: Airplane, name: "Leaves & holidays", text: "Configurable policies, quotas, encashment. India-first calendars." },
    ],
  },
  {
    title: "Workforce & Timesheets",
    tagline: "For HR outsourcing agencies",
    icon: Briefcase,
    color: "from-blue-50 to-white",
    items: [
      { icon: Briefcase, name: "Clients, projects, allocations", text: "Multi-client staffing, % capacity, billable rates, bench pipeline." },
      { icon: Clock, name: "Weekly timesheets", text: "Project-wise hours, manager approvals, auto-lock." },
      { icon: CurrencyInr, name: "Invoicing data", text: "Auto-generated invoice data per client per period." },
    ],
  },
  {
    title: "Payroll & Statutory",
    tagline: "India-ready compliance",
    icon: CurrencyInr,
    color: "from-emerald-50 to-white",
    items: [
      { icon: CurrencyInr, name: "Monthly payroll runs", text: "Configurable heads, adjustments, loans, F&F, NACH bank export." },
      { icon: ShieldCheck, name: "PF · ESIC · PT · TDS", text: "Auto-computed statutory deductions, ECR files ready for portal." },
      { icon: Users, name: "Reimbursements", text: "Multi-currency claims, approvals, reimbursement in payroll." },
    ],
  },
  {
    title: "Performance & OKR",
    tagline: "Grow talent, not spreadsheets",
    icon: Target,
    color: "from-purple-50 to-white",
    items: [
      { icon: Target, name: "KRAs with weighted KPIs", text: "Self/manager/final ratings, computed rollup, cycle-based." },
      { icon: ChartLineUp, name: "OKR — cascading", text: "Company → dept → team → individual, quarterly, live progress." },
      { icon: Users, name: "360° feedback + 9-box", text: "Multi-rater dimensions, talent grid for succession planning." },
    ],
  },
  {
    title: "Engagement",
    tagline: "Culture that people love",
    icon: Star,
    color: "from-adb-green/10 to-adb-navy/5",
    items: [
      { icon: Star, name: "Kudos + Rewards", text: "Peer recognition with value tags. Redeem points for real perks." },
      { icon: MegaphoneSimple, name: "Announcements", text: "Company feed with pinning, kinds, celebrations, policy news." },
      { icon: Sparkle, name: "Pulse & surveys", text: "1-click weekly mood + full anonymous surveys with eNPS." },
    ],
  },
  {
    title: "Operations",
    tagline: "Everything else",
    icon: Ticket,
    color: "from-orange-50 to-white",
    items: [
      { icon: Ticket, name: "Helpdesk tickets", text: "Payroll, IT, benefits, policy. SLA + status." },
      { icon: Package, name: "Asset management", text: "Issue, assign, return, condition notes." },
      { icon: GraduationCap, name: "L&D + Skills matrix", text: "Courses, enrollments, completion + 1-5 skill levels." },
    ],
  },
  {
    title: "Analytics",
    tagline: "Data that actually matters",
    icon: ChartBar,
    color: "from-adb-navy/5 to-white",
    items: [
      { icon: ChartBar, name: "Reports library", text: "Headcount, attrition, DEI, comp-equity, leaves — one click." },
      { icon: TrendUp, name: "Exit dashboard", text: "Retention risk, exit interview insights, monthly trends." },
      { icon: ChartLineUp, name: "Payroll register", text: "Audit-ready registers per period." },
    ],
  },
];


// ---------------------------------------------------------------------------
// Product Teaser · Sticky CTA
// ---------------------------------------------------------------------------
function ProductTeaser() {
  return (
    <section className="relative bg-gradient-to-b from-[#f7f6f2] to-white pt-12 pb-10" data-testid="landing-product-teaser">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zenith-teal font-bold">See it in action</div>
          <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-2">
            Every screen, <span className="font-serif-display italic text-zenith-magenta">already built.</span>
          </h3>
        </div>
        <div className="relative rounded-lg border border-zinc-200 bg-white shadow-lg overflow-hidden">
          {/* Chrome bar */}
          <div className="h-8 bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200 flex items-center gap-1.5 px-3">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
            <div className="ml-3 text-[10px] font-mono-plex text-zinc-400">zenith360.com/app/dashboard</div>
          </div>
          {/* Animated mock dashboard */}
          <div className="relative aspect-[16/8] bg-gradient-to-br from-adb-navy via-zenith-blue to-adb-navy overflow-hidden">
            <div className="absolute inset-0 orbit-grain opacity-20" />
            {/* Floating pills that animate horizontally */}
            <div className="absolute top-6 left-6 right-6 flex gap-3 animate-[slideL_18s_ease-in-out_infinite]">
              {["Attendance ↑", "Payroll Q3", "Leaves 12", "Onboarding 4", "Kudos 27"].map((t, i) => (
                <div key={t} className="px-3 py-1.5 bg-white/95 rounded-sm text-[11px] font-semibold text-adb-navy shadow-md" style={{ animationDelay: `${i * 200}ms` }}>{t}</div>
              ))}
            </div>
            {/* Stats grid */}
            <div className="absolute top-16 left-6 right-6 grid grid-cols-4 gap-3">
              {[
                { l: "Headcount", v: "412", d: "+18" },
                { l: "Present today", v: "94%", d: "+2.1" },
                { l: "Payroll ready", v: "₹28.4L", d: "on-time" },
                { l: "eNPS", v: "62", d: "↑" },
              ].map((s, i) => (
                <div key={s.l} className="p-3 bg-white/10 backdrop-blur-sm border border-white/15 rounded-sm text-white">
                  <div className="text-[10px] uppercase tracking-widest text-zenith-green/90 font-bold">{s.l}</div>
                  <div className="font-display text-2xl font-bold mt-1">{s.v}</div>
                  <div className="text-[10px] text-white/70 mt-0.5">{s.d}</div>
                </div>
              ))}
            </div>
            {/* Animated line chart */}
            <svg viewBox="0 0 600 120" className="absolute bottom-6 left-6 right-6 h-24 w-[calc(100%-3rem)]" fill="none">
              <defs>
                <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#33cc33" stopOpacity="0.4" />
                  <stop offset="100%" stopColor="#33cc33" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d="M0,90 C60,72 120,80 180,60 C240,40 300,55 360,42 C420,28 480,48 540,25 L600,20 L600,120 L0,120 Z" fill="url(#chartFill)" />
              <path d="M0,90 C60,72 120,80 180,60 C240,40 300,55 360,42 C420,28 480,48 540,25 L600,20"
                stroke="#33cc33" strokeWidth="2" fill="none"
                strokeDasharray="1200" strokeDashoffset="1200"
                style={{ animation: "drawLine 3.5s ease-in-out infinite alternate" }} />
              {[[0,90],[180,60],[360,42],[540,25]].map(([x,y], i) => (
                <circle key={i} cx={x} cy={y} r="3" fill="#33cc33" />
              ))}
            </svg>
          </div>
        </div>
        <style>{`
          @keyframes drawLine { 0% { stroke-dashoffset: 1200; } 100% { stroke-dashoffset: 0; } }
          @keyframes slideL { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-32px); } }
        `}</style>
      </div>
    </section>
  );
}

function StickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      data-testid="landing-sticky-cta"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"}`}
    >
      <div className="bg-white/95 backdrop-blur border-b border-zinc-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-3">
          <a href="#top" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-white rounded-sm p-0.5 border border-zinc-200 grid place-items-center">
              <img src="/zenith360-mark.png" alt="Zenith360" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="font-display text-sm font-bold leading-none">
              Zenith<span className="text-zenith-green">360</span> HR
            </div>
          </a>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-adb-navy font-semibold hover:text-adb-navy-800 px-2 py-1.5" data-testid="sticky-signin">
              Sign in
            </Link>
            <Link to="/signup" className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-adb-navy to-zenith-blue text-white rounded-sm text-xs font-semibold hover:brightness-110" data-testid="sticky-signup">
              Start free <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


function PricingSection() {  const [packs, setPacks] = useState([]);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/sales/pricing`).then((r) => setPacks(r.data.packs || [])).catch(() => setPacks([]));
  }, []);
  const fmt = (n) => n == null ? "Contact us" : `₹${n}`;
  return (
    <section id="pricing" className="py-20 bg-white border-t border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.3em] text-zenith-teal font-bold">Simple pricing</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">
            One number. <span className="font-serif-display italic text-zenith-magenta">Per seat, per month.</span>
          </h2>
          <p className="text-zinc-600 mt-3 text-base">
            Start on Essentials — upgrade when your team grows. Cancel anytime.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {packs.map((p) => (
            <div
              key={p.code}
              data-testid={`landing-pack-${p.code}`}
              className={`relative rounded-md border bg-white p-6 flex flex-col ${
                p.highlight
                  ? "border-zenith-magenta shadow-[0_0_0_1px] shadow-zenith-magenta/20 md:scale-[1.02]"
                  : "border-zinc-200"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-6">
                  <Badge variant="outline" className="rounded-sm bg-zenith-magenta text-white border-zenith-magenta font-mono-plex text-[10px] uppercase tracking-widest">
                    Most popular
                  </Badge>
                </div>
              )}
              <div className="text-[11px] uppercase tracking-widest font-bold text-zenith-teal">{p.target}</div>
              <div className="font-display text-3xl font-bold text-adb-navy mt-1">{p.name}</div>

              <div className="mt-4">
                <span className="font-display text-5xl font-bold text-adb-navy">{fmt(p.price_inr)}</span>
                {p.price_inr != null && <span className="text-sm text-zinc-500"> /seat / month</span>}
                {p.annual_discount_percent > 0 && (
                  <div className="text-xs text-zenith-green font-semibold mt-1">
                    Save {p.annual_discount_percent}% billed annually
                  </div>
                )}
              </div>

              <p className="text-sm text-zinc-600 mt-4">{p.tagline}</p>

              <ul className="text-sm mt-5 space-y-1.5 flex-1">
                {p.modules.map((m) => (
                  <li key={m} className="flex items-start gap-1.5">
                    <CheckCircle size={14} weight="fill" className="text-zenith-green mt-0.5 shrink-0" /> {m}
                  </li>
                ))}
              </ul>

              <Link
                to={p.price_inr == null ? "/careers?company=acme" : "/login"}
                data-testid={`landing-pack-cta-${p.code}`}
                className={`mt-6 inline-flex items-center justify-center gap-1 px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${
                  p.highlight
                    ? "bg-adb-navy text-white hover:bg-adb-navy-800"
                    : "border border-adb-navy text-adb-navy hover:bg-adb-navy hover:text-white"
                }`}
              >
                {p.price_inr == null ? "Talk to sales" : "Start free trial"} <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-8 max-w-3xl mx-auto text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2.5 rounded-sm bg-zenith-teal/5 border border-zenith-teal/20 text-xs">
            <span className="font-semibold text-adb-navy inline-flex items-center gap-1.5">
              <CheckCircle size={14} weight="fill" className="text-zenith-green" /> Valid with minimum 3 users
            </span>
            <span className="text-zinc-300">·</span>
            <span className="font-semibold text-adb-navy inline-flex items-center gap-1.5">
              <CheckCircle size={14} weight="fill" className="text-zenith-green" /> Admin Login is free
            </span>
          </div>
          <div className="mt-3 text-xs text-zinc-500">
            All prices in INR. Prices exclude 18% GST. Feature-gated on the backend — upgrade unlocks instantly.
          </div>
        </div>
      </div>
    </section>
  );
}


export default function Landing() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("core-hr");
  const [darkHero, setDarkHero] = useState(false);
  const signin = async (e, credOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    const em = credOverride?.email || email;
    const pw = credOverride?.password || password;
    setBusy(true);
    const res = await login(em, pw);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav("/app", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  const activeCat = CATEGORIES.find((c) => c.title.toLowerCase().replace(/\s+&\s+|\s+/g, "-") === tab) || CATEGORIES[0];

  return (
    <div className="min-h-screen bg-[#f7f6f2]">
      {/* Sticky top nav */}
      <nav className="sticky top-0 z-30 backdrop-blur bg-[#f7f6f2]/85 border-b border-zinc-200/70">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="landing-brand">
            <div className="w-14 h-14 bg-white rounded-md p-1.5 border border-zinc-200 shadow-sm grid place-items-center transition-transform group-hover:scale-105">
              <img src="/zenith360-mark.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight leading-none">Zenith<span className="text-zenith-green">360</span> <span className="text-adb-navy">HR</span></div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Your complete HR Ecosystem</div>
            </div>
          </a>
          <div className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-zinc-600 hover:text-adb-navy">Features</a>
            <a href="#how" className="text-sm text-zinc-600 hover:text-adb-navy">How it works</a>
            <a href="#compliance" className="text-sm text-zinc-600 hover:text-adb-navy">Compliance</a>
            <a href="#pricing" className="text-sm text-zinc-600 hover:text-adb-navy">Pricing</a>
            <a href="#ai" className="text-sm text-zinc-600 hover:text-adb-navy">Ask Zenith360</a>
            <Link to="/help" className="text-sm text-zinc-600 hover:text-adb-navy" data-testid="landing-nav-help">Help &amp; docs</Link>
            <a href="#faq" className="text-sm text-zinc-600 hover:text-adb-navy">FAQ</a>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Link to="/login" className="px-3 py-2 text-sm text-adb-navy font-semibold hover:text-adb-navy-800" data-testid="landing-signin-top">
              Sign in
            </Link>
            <Link to="/signup" className="px-4 py-2 bg-gradient-to-r from-adb-navy to-zenith-blue text-white rounded-sm text-sm font-semibold hover:brightness-110 transition-all inline-flex items-center gap-1.5" data-testid="landing-signup-top">
              Start free <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </nav>

      <StickyCTA />

      {/* Hero */}
      <section id="top" className={`relative overflow-hidden zenith-blobs transition-colors duration-500 ${darkHero ? "bg-gradient-to-br from-[#0a1830] via-adb-navy to-[#0a1830] text-white" : ""}`}>
        <button
          type="button"
          data-testid="landing-dark-toggle"
          onClick={() => setDarkHero((v) => !v)}
          title={darkHero ? "Switch to light hero" : "Preview dark hero"}
          className={`absolute top-4 right-4 md:top-6 md:right-6 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
            darkHero
              ? "bg-white/10 border-white/20 text-white hover:bg-white/15"
              : "bg-white border-zinc-200 text-adb-navy hover:border-adb-navy/40"
          }`}
        >
          {darkHero ? <><Sun size={13} weight="fill" /> Light</> : <><Moon size={13} weight="fill" /> Dark</>}
        </button>
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="fade-up">
            {/* Prominent Zenith360 logo mark */}
            <div className="flex items-center gap-4 mb-6" data-testid="landing-hero-logo">
              <div className="relative">
                <div className={`absolute -inset-2 rounded-2xl blur-xl ${darkHero ? "bg-gradient-to-br from-zenith-teal/40 via-zenith-magenta/40 to-zenith-green/40" : "bg-gradient-to-br from-zenith-teal/25 via-zenith-magenta/25 to-zenith-green/25"}`} />
                <div className={`relative w-40 h-28 md:w-48 md:h-36 rounded-xl shadow-lg p-2 grid place-items-center transition-colors ${darkHero ? "bg-white/95 border border-white/30" : "bg-white border border-zinc-200 shadow-adb-navy/5"}`}>
                  <img src="/zenith360-logo.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
                </div>
              </div>
              <div>
                <div className={`font-display text-3xl md:text-4xl font-bold tracking-tight leading-none ${darkHero ? "text-white" : ""}`}>Zenith<span className="text-zenith-green">360</span> <span className={darkHero ? "text-white" : "text-adb-navy"}>HR</span></div>
                <div className={`text-[11px] uppercase tracking-[0.28em] font-bold mt-2 ${darkHero ? "text-zenith-green" : "text-zenith-teal"}`}>Your complete HR Ecosystem</div>
              </div>
            </div>
            <Badge variant="outline" className={`rounded-sm font-mono-plex text-xs uppercase tracking-widest ${darkHero ? "bg-white/10 border-zenith-green/60 text-zenith-green" : "bg-gradient-to-r from-zenith-teal/10 to-zenith-magenta/10 border-zenith-teal/40 text-adb-navy"}`}>
              Multi-tenant HRMS · India-first
            </Badge>
            <h1 className={`mt-5 font-display text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.02] ${darkHero ? "text-white" : "text-zinc-950"}`}>
              The HR platform for<br />
              <span className="font-serif-display italic bg-gradient-to-r from-zenith-blue via-zenith-magenta to-zenith-teal bg-clip-text text-transparent">every company</span> you<br />
              run HR for.
            </h1>
            <p className={`mt-6 text-lg leading-relaxed max-w-lg ${darkHero ? "text-white/75" : "text-zinc-600"}`}>
              Zenith360 HR is the multi-tenant, India-first HR ecosystem for agencies and the clients they serve — attendance, payroll, statutory, performance, engagement, and analytics in a single, beautiful workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="landing-cta-signup" className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-adb-navy to-zenith-blue text-white rounded-sm font-semibold hover:brightness-110 transition-all group shadow-sm">
                Start 14-day free trial <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/login" data-testid="landing-cta-signin" className="inline-flex items-center gap-2 px-5 py-3 border border-adb-navy/30 text-adb-navy rounded-sm font-semibold hover:bg-white transition-colors">
                I have an account
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 flex-wrap">
              {["Multi-tenant", "India-first payroll", "PF · ESIC · PT · TDS", "AI copilot"].map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-xs text-zinc-500">
                  <ShieldCheck size={14} className="text-adb-green" weight="fill" /> {f}
                </div>
              ))}
            </div>
            {/* Stats strip */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {STATS.map((s, i) => {
                const tones = ["teal", "magenta", "green", "blue"];
                const t = TONE[tones[i % 4]];
                return (
                  <div key={s.label} className={`border ${t.border} ${t.bg} rounded-sm p-3`}>
                    <div className={`font-display font-bold text-2xl ${t.text}`}>{s.n}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5">{s.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right rail: sign-in card */}
          <div id="signin" className="fade-up">
            <div className="border border-zinc-200 bg-white rounded-sm p-7 shadow-sm relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-adb-green/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-bold">Sign in</div>
                <div className="font-display text-2xl font-bold mt-1">Welcome back.</div>
                <div className="text-sm text-zinc-500 mt-1">Use your work email and password.</div>
                <form onSubmit={signin} className="mt-5 space-y-3">
                  <div><label className="text-xs text-zinc-600">Work email</label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="rounded-sm mt-1" data-testid="landing-email" /></div>
                  <div><label className="text-xs text-zinc-600">Password</label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="rounded-sm mt-1" data-testid="landing-password" /></div>
                  <Button type="submit" disabled={busy} className="w-full bg-adb-navy hover:bg-adb-navy-800 rounded-sm h-11 font-semibold" data-testid="landing-submit">
                    {busy ? "Signing in…" : "Sign in →"}
                  </Button>
                </form>
                <div className="mt-5 pt-4 border-t border-zinc-100">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Demo accounts · Admin@12345</div>
                  <div className="mt-2 space-y-1">
                    {[
                      { r: "Super Admin", e: "superadmin@agency.hr" },
                      { r: "HR", e: "hr@acme.hr" },
                      { r: "Employee", e: "arjun.iyer@acme.hr" },
                    ].map((d) => (
                      <button key={d.e} onClick={() => signin(null, { email: d.e, password: "Admin@12345" })} disabled={busy} className="w-full flex items-center justify-between px-2 py-1.5 border border-zinc-100 hover:border-adb-navy hover:bg-adb-navy/5 rounded-sm text-xs group" data-testid={`landing-demo-${d.r.toLowerCase().replace(/\s+/g, "-")}`}>
                        <span><span className="font-semibold">{d.r}</span> · <span className="font-mono-plex text-zinc-500">{d.e}</span></span>
                        <span className="text-adb-navy font-semibold opacity-0 group-hover:opacity-100 transition-opacity">Fill →</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingTeaser />

      {/* Personas — Built for */}
      <section id="built-for" className="bg-white border-t border-zinc-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-adb-navy font-bold">Built for</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">
              Three <span className="font-serif-display italic bg-gradient-to-r from-zenith-teal via-zenith-magenta to-zenith-green bg-clip-text text-transparent">roles</span>. One product.
            </h2>
            <p className="text-zinc-600 mt-4">Zenith360 HR is designed so agencies, HR leaders, and employees each get a purpose-built experience.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="landing-personas">
            {PERSONAS.map((p) => {
              const t = TONE[p.tone];
              const Icon = p.icon;
              return (
                <div key={p.tag} className={`relative border ${t.border} ${t.bg} rounded-sm p-6 overflow-hidden`} data-testid={`persona-${p.tone}`}>
                  <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${t.dot} opacity-15 blur-2xl`} />
                  <div className="relative">
                    <div className={`w-11 h-11 rounded-sm bg-white border ${t.border} grid place-items-center`}>
                      <Icon size={22} className={t.text} weight="duotone" />
                    </div>
                    <div className={`mt-4 text-[10px] uppercase tracking-[0.25em] font-bold ${t.text}`}>{p.tag}</div>
                    <div className="font-display text-xl font-bold text-zinc-900 mt-1.5 leading-snug">{p.title}</div>
                    <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{p.text}</p>
                    <ul className="mt-4 space-y-1.5">
                      {p.bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-xs text-zinc-700">
                          <CheckCircle size={14} weight="fill" className={t.text} /> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features (tabs) */}
      <section id="features" className="bg-white border-t border-b border-zinc-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-adb-navy font-bold">Everything HR needs</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">
              Eight <span className="font-serif-display italic text-adb-navy">essential</span> pillars.<br />
              One coherent product.
            </h2>
            <p className="text-zinc-600 mt-4">Explore each category — every feature ships end-to-end, browser and mobile-friendly, ready to configure per tenant.</p>
          </div>

          {/* Category tabs */}
          <div className="mt-10 flex flex-wrap gap-2" data-testid="landing-cat-tabs">
            {CATEGORIES.map((c) => {
              const key = c.title.toLowerCase().replace(/\s+&\s+|\s+/g, "-");
              const active = tab === key;
              return (
                <button key={key} onClick={() => setTab(key)} data-testid={`landing-cat-${key}`}
                  className={`px-4 py-2 rounded-sm text-sm font-semibold border transition-all flex items-center gap-2 ${active ? "bg-adb-navy text-white border-adb-navy" : "bg-white border-zinc-200 hover:border-adb-navy/40 text-zinc-700"}`}>
                  <c.icon size={14} weight={active ? "fill" : "regular"} /> {c.title}
                </button>
              );
            })}
          </div>

          {/* Active category showcase */}
          <div className={`mt-8 rounded-sm border border-zinc-200 bg-gradient-to-br ${activeCat.color} p-8`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-sm bg-adb-navy grid place-items-center">
                <activeCat.icon size={24} className="text-adb-green" weight="fill" />
              </div>
              <div>
                <div className="font-display text-2xl font-bold text-adb-navy">{activeCat.title}</div>
                <div className="text-sm text-zinc-600 italic font-serif-display">{activeCat.tagline}</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {activeCat.items.map((it) => (
                <div key={it.name} data-testid={`landing-feature-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 24)}`} className="bg-white border border-zinc-200 rounded-sm p-5 hover:border-adb-navy/40 hover:-translate-y-0.5 transition-all">
                  <it.icon size={20} className="text-adb-navy" weight="duotone" />
                  <div className="font-display font-bold text-base mt-3">{it.name}</div>
                  <div className="text-xs text-zinc-600 mt-1.5 leading-relaxed">{it.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works — 3 steps */}
      <section id="how" className="py-20 bg-[#f7f6f2] relative overflow-hidden">
        <div className="absolute -top-24 left-1/4 w-96 h-96 rounded-full bg-zenith-teal/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 rounded-full bg-zenith-magenta/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-adb-navy font-bold">How it works</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">
              From zero to <span className="font-serif-display italic bg-gradient-to-r from-zenith-blue via-zenith-magenta to-zenith-green bg-clip-text text-transparent">running payroll</span> in a day.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="landing-how">
            {HOW_IT_WORKS.map((step, i) => {
              const t = TONE[step.tone];
              const Icon = step.icon;
              return (
                <div key={step.n} className="relative">
                  <div className="border border-zinc-200 bg-white rounded-sm p-6 h-full">
                    <div className="flex items-start justify-between">
                      <div className={`w-14 h-14 rounded-sm ${t.bg} border ${t.border} grid place-items-center`}>
                        <Icon size={28} className={t.text} weight="duotone" />
                      </div>
                      <div className={`font-mono-plex text-3xl font-bold ${t.text}/40`}>{step.n}</div>
                    </div>
                    <div className="font-display text-xl font-bold mt-5">{step.title}</div>
                    <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{step.text}</p>
                  </div>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <ArrowRight size={22} className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 text-zinc-300" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI section */}
      <section id="ai" className="py-20 bg-adb-navy relative overflow-hidden">
        <div className="absolute inset-0 orbit-grain opacity-30" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
            <div className="w-20 h-20 rounded-full bg-adb-green grid place-items-center shrink-0">
              <Sparkle size={34} weight="fill" className="text-adb-navy" />
            </div>
            <div className="text-white">
              <div className="text-xs uppercase tracking-[0.3em] text-adb-green font-bold">Built-in AI</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2">
                Meet <span className="font-serif-display italic text-adb-green">Ask Zenith360</span>.
              </h2>
              <p className="mt-4 text-white/80 text-lg leading-relaxed max-w-2xl">
                A Claude Sonnet 4.5-powered HR concierge lives in every screen. Ask policy questions, generate letter templates, draft policies with one click, or explain any HR concept in seconds.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { icon: Question, t: "Policy answers", d: "How many casual leaves do I get? How is PF computed?" },
                  { icon: MegaphoneSimple, t: "Draft letters", d: "One-click AI-generated offer, appointment, PIP letters." },
                  { icon: ShieldCheck, t: "Compliance help", d: "Explain POSH, IT Act, Shops & Establishments Act." },
                ].map((f) => (
                  <div key={f.t} className="border border-white/15 backdrop-blur-sm bg-white/5 rounded-sm p-4">
                    <f.icon size={18} className="text-adb-green" weight="fill" />
                    <div className="font-display font-bold text-sm mt-2">{f.t}</div>
                    <div className="text-xs text-white/70 mt-1">{f.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance & Security */}
      <section id="compliance" className="py-20 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-10">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zenith-blue-500 font-bold">Compliance-first</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 leading-[1.05]">
                India statutory. <span className="font-serif-display italic bg-gradient-to-r from-zenith-teal to-zenith-green bg-clip-text text-transparent">Built in.</span>
              </h2>
              <p className="text-zinc-600 mt-4 leading-relaxed">
                Every payroll run computes PF, ESIC, PT, and TDS as per current rates. Statutory returns generate as ready-to-upload files. Every policy references the underlying Indian law.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <a href="#signin" className="inline-flex items-center gap-2 px-4 py-2.5 bg-adb-navy text-white rounded-sm text-sm font-semibold hover:bg-adb-navy-800 transition-colors">
                  Start free <ArrowRight size={14} />
                </a>
                <a href="#faq" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-300 rounded-sm text-sm font-semibold hover:bg-zinc-50 transition-colors">
                  Read FAQ
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="landing-compliance-grid">
              {COMPLIANCE_ITEMS.map((c, i) => {
                const tones = ["blue", "teal", "magenta", "green", "blue", "teal"];
                const t = TONE[tones[i]];
                const Icon = c.icon;
                return (
                  <div key={c.t} className={`border ${t.border} ${t.bg} rounded-sm p-4 hover:-translate-y-0.5 transition-transform`}>
                    <Icon size={18} className={t.text} weight="duotone" />
                    <div className="font-display font-bold text-sm mt-2 text-zinc-900">{c.t}</div>
                    <div className="text-[11px] text-zinc-600 mt-1 leading-snug">{c.d}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <PricingSection />

      {/* FAQ */}
      <section id="faq" className="py-20 bg-[#f7f6f2]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs uppercase tracking-[0.3em] text-adb-navy font-bold">Frequently asked</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">
              Answers to the <span className="font-serif-display italic text-zenith-magenta">real</span> questions.
            </h2>
          </div>
          <div className="mt-10 space-y-2" data-testid="landing-faq">
            {FAQS.map((f, i) => (
              <details key={i} className="border border-zinc-200 bg-white rounded-sm group" data-testid={`landing-faq-${i}`}>
                <summary className="px-5 py-4 flex items-center justify-between cursor-pointer text-sm font-semibold text-zinc-900 select-none">
                  <span className="pr-4">{f.q}</span>
                  <span className="w-6 h-6 grid place-items-center rounded-full bg-zenith-teal/10 text-zenith-teal group-open:rotate-45 transition-transform text-lg font-normal">+</span>
                </summary>
                <div className="px-5 pb-4 text-sm text-zinc-600 leading-relaxed border-t border-zinc-100 pt-3">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Help & Docs teaser */}
      <section id="help-docs" className="relative bg-white border-t border-zinc-200 py-20" data-testid="landing-help-docs">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-zenith-teal font-bold">Product help center</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy mt-3 leading-[1.05]">
                A living, breathing <span className="font-serif-display italic text-zenith-magenta">product brochure.</span>
              </h2>
              <p className="text-zinc-600 mt-4 leading-relaxed">
                Every screen. Every workflow. Every API. Zenith360 HR auto-generates a searchable help center and a
                downloadable PDF brochure — re-rendered by AI whenever we ship a new feature. No stale marketing
                deck. No outdated screenshots.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/help"
                  data-testid="landing-help-explore"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-adb-navy to-zenith-blue text-white rounded-sm font-semibold hover:brightness-110 transition-all"
                >
                  Explore the help center <ArrowRight size={15} />
                </Link>
                <a
                  href="/api/help/brochure.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="landing-help-brochure"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-zenith-magenta/40 text-zenith-magenta bg-white rounded-sm font-semibold hover:bg-zenith-magenta/5 transition-all"
                >
                  Download brochure (PDF)
                </a>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-xs text-zinc-500">
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zenith-green" /> Auto-regenerates on every deploy</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zenith-teal" /> Filter by end-user · admin · prospect</span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-zenith-magenta" /> AI-written copy · India-first</span>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-zenith-teal/10 via-zenith-magenta/10 to-zenith-green/10 rounded-lg blur-2xl" />
              <div className="relative rounded-lg border border-zinc-200 bg-white shadow-xl p-6">
                <div className="text-[10px] uppercase tracking-widest text-zenith-magenta font-bold mb-3">Live from the manifest</div>
                <div className="space-y-2">
                  {[
                    { k: "People", v: "Employees · Org chart · Onboarding" },
                    { k: "Time & Attendance", v: "Geo check-in · Leaves · Shifts · Compliance" },
                    { k: "Workforce", v: "Clients · Projects · Allocations · Timesheets" },
                    { k: "PMO Suite", v: "Billability · Invoicing · Statutory" },
                    { k: "Payroll", v: "Runs · Payslips · PF/ESIC/PT/TDS · Reimbursements" },
                    { k: "Talent", v: "Recruitment · ATS · Performance · L&D · AI Studio" },
                    { k: "Engagement", v: "Kudos · Rewards · Announcements · Surveys" },
                    { k: "Admin", v: "RBAC · Billing · Sales Console · Audit log" },
                  ].map((row) => (
                    <div key={row.k} className="flex items-baseline gap-3 py-1.5 border-b border-zinc-100 last:border-b-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-zenith-teal flex-shrink-0" />
                      <div className="font-display font-bold text-adb-navy text-sm min-w-[130px]">{row.k}</div>
                      <div className="text-xs text-zinc-600 leading-relaxed">{row.v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-dashed border-zinc-200 text-[11px] text-zinc-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-zenith-green animate-pulse" />
                  Manifest re-rendered by AI on every deploy · 52+ features indexed
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA band */}
      <section className="relative overflow-hidden">
        <div className="bg-gradient-to-r from-zenith-blue via-adb-navy to-zenith-blue-700 py-20">
          <div className="absolute inset-0">
            <div className="absolute -top-24 left-1/4 w-96 h-96 rounded-full bg-zenith-magenta/20 blur-3xl" />
            <div className="absolute -bottom-24 right-1/4 w-96 h-96 rounded-full bg-zenith-teal/20 blur-3xl" />
          </div>
          <div className="max-w-4xl mx-auto px-6 text-center relative">
            <div className="text-xs uppercase tracking-[0.3em] text-zenith-green font-bold">Ready when you are</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 text-white leading-[1.05]">
              Run HR the <span className="font-serif-display italic bg-gradient-to-r from-zenith-teal via-white to-zenith-green bg-clip-text text-transparent">Zenith360</span> way.
            </h2>
            <p className="text-white/80 mt-4 max-w-2xl mx-auto">Sign in with one of the demo accounts on this page and take the full tour — or use the Setup Wizard to spin up your own tenant in 30 seconds.</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <a href="#signin" className="inline-flex items-center gap-2 px-6 py-3 bg-zenith-green text-adb-navy rounded-sm font-bold hover:brightness-105 transition-all">
                Start free · Sign in <ArrowRight size={16} />
              </a>
              <a href="#features" className="inline-flex items-center gap-2 px-6 py-3 border border-white/30 text-white rounded-sm font-semibold hover:bg-white/10 transition-colors">
                Explore features
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 bg-[#0f2e57] text-white/70">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-md p-1 grid place-items-center shadow-sm">
                <img src="/zenith360-mark.png" alt="Zenith360" className="max-w-full max-h-full object-contain" />
              </div>
              <div className="font-display font-bold text-xl text-white">Zenith360 HR</div>
            </div>
            <div className="text-xs mt-2">© {new Date().getFullYear()} Zenith360 HR · <span className="italic text-zenith-green">Your complete HR Ecosystem</span></div>
          </div>
          <div className="flex gap-6 text-xs flex-wrap">
            <Link to="/login" className="hover:text-white">Sign in</Link>
            <a href="#features" className="hover:text-white">Features</a>
            <a href="#pricing" className="hover:text-white">Pricing</a>
            <Link to="/help" className="hover:text-white" data-testid="landing-footer-help">Help &amp; docs</Link>
            <a href="/api/help/brochure.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="landing-footer-brochure">Brochure PDF</a>
            <Link to="/privacy" className="hover:text-white" data-testid="landing-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="landing-footer-terms">Terms</Link>
            <Link to="/delete-account" className="hover:text-white" data-testid="landing-footer-delete">Delete account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}